import { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';

const API_URL = import.meta.env.VITE_API_URL || '/api';

export type ScanResultType = 'order' | 'drop' | 'bin' | 'work_order' | 'part' | 'unknown';

export interface ScanRouteResult {
  type: ScanResultType;
  message: string;
  navigated: boolean;
}

/**
 * Routes scanned barcodes to the correct page with context.
 *
 * Barcode formats:
 *   VT26020003        → Order number → /orders?search=VT26020003
 *   DRP-20260207-0001 → Drop ID → /drops?dropId=DRP-...
 *   BIN-001 or 3-6 digits → Bin number → /scan?barcode=BIN-001
 *   UUID              → Work order → /work/:id
 *   4-6 digit number  → Part number → /inventory?search=11001
 */
export function useBarcodeRouter() {
  const navigate = useNavigate();

  const routeBarcode = useCallback(async (barcode: string): Promise<ScanRouteResult> => {
    const token = localStorage.getItem('auth_token');
    const upper = barcode.toUpperCase().trim();

    try {
      // Drop barcode: DRP-YYYYMMDD-XXXX
      if (upper.match(/^DRP-\d{8}-\d{4}$/)) {
        const response = await fetch(`${API_URL}/inventory/drops/by-drop-id/${upper}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (response.ok) {
          navigate(`/drops?dropId=${upper}`);
          return { type: 'drop', message: `Drop ${upper} found`, navigated: true };
        }
        return { type: 'unknown', message: `Drop ${upper} not found`, navigated: false };
      }

      // Order number: VT + digits
      if (upper.match(/^VT\d{8,}/)) {
        const response = await fetch(`${API_URL}/orders/number/${upper}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (response.ok) {
          navigate(`/orders?search=${encodeURIComponent(upper)}`);
          return { type: 'order', message: `Order ${upper}`, navigated: true };
        }
        return { type: 'unknown', message: `Order ${upper} not found`, navigated: false };
      }

      // Bin: BIN-XXX or 3-6 digits
      if (upper.match(/^BIN-?\d+$/) || upper.match(/^\d{3,6}$/)) {
        navigate(`/scan?barcode=${encodeURIComponent(upper)}`);
        return { type: 'bin', message: `Bin ${upper}`, navigated: true };
      }

      // UUID pattern — try as work order
      if (upper.match(/^[0-9A-F]{8}-[0-9A-F]{4}-[0-9A-F]{4}-[0-9A-F]{4}-[0-9A-F]{12}$/i)) {
        const response = await fetch(`${API_URL}/production/work-order/${barcode}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (response.ok) {
          navigate(`/work/${barcode}`);
          return { type: 'work_order', message: 'Work order found', navigated: true };
        }
        return { type: 'unknown', message: 'Work order not found', navigated: false };
      }

      // Part number pattern (e.g., 11001, 11001.0)
      if (upper.match(/^\d{4,6}(\.\d)?$/)) {
        navigate(`/inventory?search=${encodeURIComponent(upper)}`);
        return { type: 'part', message: `Part ${upper}`, navigated: true };
      }

      return { type: 'unknown', message: `Unknown barcode: ${barcode}`, navigated: false };
    } catch (err) {
      console.error('Barcode routing error:', err);
      return { type: 'unknown', message: 'Scan failed — check connection', navigated: false };
    }
  }, [navigate]);

  return { routeBarcode };
}
